import { ArrowDownRight, ArrowUpRight, Minus, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toneClasses } from "@/components/ui/badgeTones";
import { cn } from "@/lib/cn";
import { StatusBadge } from "@/components/ui/StatusBadge";

interface Props {
  /**
   * Signed percentage change vs the previous window. `null` (no baseline)
   * renders either the "New" label (when {@link isNew}) or a neutral em dash.
   */
  changePct: number | null;
  /**
   * True when there is current activity but ZERO prior activity (the backend
   * `*_is_new` flag). Only meaningful when `changePct` is null: it switches the
   * neutral em dash for an explicit "New" label so a fresh metric is not
   * mistaken for "no change" (TASK-3692).
   */
  isNew?: boolean;
  /**
   * True when `changePct` is non-null but its magnitude exceeds 999% — a giant
   * ratio off a small-but-nonzero baseline (the backend `*_capped` flag). The
   * badge then renders an abbreviated ">+999%" / "<-999%" label instead of the
   * misleading exact figure so low-seed noise does not read as explosive growth
   * (BUG-192). Ignored when `changePct` is null.
   */
  capped?: boolean;
  /**
   * Flip the positive/negative coloring for down-is-good metrics (churn, cost,
   * overdue, late %). When true, a negative `changePct` is colored green and a
   * positive one red — the direction icon and label are unchanged, only the
   * tone swaps. Has no effect on the null/zero/"New" cases. Added for the rich
   * `KpiCard` layout (TASK-3880).
   */
  invert?: boolean;
  className?: string;
}

/**
 * Compact signed delta badge for a flat (white) trend KPI card.
 * Render rule (TASK-3692):
 *  - `changePct != null && capped` → ">+999%" (up) / "<-999%" (down) — BUG-192.
 *  - `changePct != null` → "+N%" (green up) / "-N%" (red down) / "0%" (neutral).
 *  - `changePct == null && isNew` → "New" (the prior window had no activity).
 *  - `changePct == null && !isNew` → "—" (no baseline at all).
 * The accessible label is sourced from i18n so the direction is announced.
 */
export function DeltaBadge({
  changePct,
  isNew = false,
  capped = false,
  invert = false,
  className,
}: Props) {
  const { t } = useTranslation();

  if (changePct === null) {
    if (isNew) {
      return (
        <StatusBadge
          tone="info"
          className={className}
          title={t("adminPanel.dashboard.delta.new")}
        >
          <Sparkles className="h-3 w-3" aria-hidden="true" />
          {t("adminPanel.dashboard.delta.newLabel")}
        </StatusBadge>
      );
    }
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-500",
          className,
        )}
        title={t("adminPanel.dashboard.delta.noBaseline")}
      >
        <Minus className="h-3 w-3" aria-hidden="true" />—
      </span>
    );
  }

  const isUp = changePct > 0;
  const isDown = changePct < 0;
  const Icon = isUp ? ArrowUpRight : isDown ? ArrowDownRight : Minus;
  const sign = isUp ? "+" : "";
  // `invert` swaps the green/red tones for down-is-good metrics: a decrease is
  // then "good" (green) and an increase "bad" (red). The icon/label/sign keep
  // reflecting the raw numeric direction.
  // Badge tones (TASK-5133, DEV_RULES §10.10).
  const good = toneClasses(invert ? "danger" : "success");
  const bad = toneClasses(invert ? "success" : "danger");
  const tone = isUp ? good : isDown ? bad : "bg-gray-100 text-gray-500";
  // BUG-192: a capped percentage is abbreviated to ">+999%" / "<-999%" so a
  // giant ratio off a tiny baseline never reads as exact explosive growth.
  const title = capped
    ? isUp
      ? t("adminPanel.dashboard.delta.cappedUp")
      : t("adminPanel.dashboard.delta.cappedDown")
    : isUp
      ? t("adminPanel.dashboard.delta.up", { value: changePct })
      : isDown
        ? t("adminPanel.dashboard.delta.down", { value: Math.abs(changePct) })
        : t("adminPanel.dashboard.delta.flat");
  const label = capped ? (isUp ? ">+999%" : "<-999%") : `${sign}${changePct}%`;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold",
        tone,
        className,
      )}
      title={title}
    >
      <Icon className="h-3 w-3" aria-hidden="true" />
      {label}
    </span>
  );
}
